class RecentlyViewedSwiper {
  constructor() {
    this.key = 'recentlyViewedProducts';
    this.sectionEl = document.querySelector('#Section-recently-viewed');
    if (!this.sectionEl) return;

    const days = Number(this.sectionEl.getAttribute('data-expire')) || 7;
    this.expireMs = days * 24 * 60 * 60 * 1000;
    this.maxItems = 6;
    this.wrapper = this.sectionEl.querySelector('[data-slides]');
    this.wrapper.classList.add('swiper-wrapper');

    if (document.readyState !== 'loading') this.init();
    else document.addEventListener('DOMContentLoaded', () => this.init());
  }

  init() {
    this.trackCurrentProduct();

    const currentHandle = window.currentProduct.handle;
    const items = this.load()
      .filter((i) => i.handle !== currentHandle)
      .reverse();

    if (!items.length) return;
    this.renderSlides(items);
    this.sectionEl.style.display = '';
    this.initSwiper();
  }

  trackCurrentProduct() {
    const { handle, child_products, badges } = window.currentProduct;
    let list = this.load().filter((i) => i.handle !== handle);
    list.push({ handle, child_products, badges, timestamp: Date.now() });
    if (list.length > this.maxItems) list = list.slice(-this.maxItems);
    localStorage.setItem(this.key, JSON.stringify(list));
  }

  load() {
    const raw = localStorage.getItem(this.key);
    if (!raw) return [];
    try {
      const items = JSON.parse(raw);
      const cutoff = Date.now() - this.expireMs;
      return items.filter((i) => i.timestamp > cutoff);
    } catch {
      return [];
    }
  }

  async renderSlides(products) {
    const sizes =
      '(min-width:1200px)267px,(min-width:1040px)calc((100vw-130px)/4),(min-width:768px)calc((100vw-120px)/3),calc((100vw-35px)/2)';

    const formatMoney = (cents) => {
      const fmt = window.recentlyViewedMoneyFormat;
      const amount = (Number(cents) / 100).toFixed(2);
      return fmt.replace(/{{\s*amount\s*}}/, amount);
    };

    for (const { handle, child_products, badges } of products) {
      try {
        const product = await fetch(window.Shopify.routes.root + `products/${handle}.js`).then((r) => r.json());
        const variant = (product.variants && product.variants[0]) || {};
        const lowest_price = Array.isArray(child_products)
          ? Math.min(...child_products.map((child) => Number(child.price) || Infinity))
          : null;

        let out_of_stock = false;

        if (Array.isArray(child_products) && child_products.length > 0) {
          out_of_stock = child_products.every((child) => child.variants?.[0]?.available === false);
        } else {
          out_of_stock = variant?.available === false;
        }

        const p = {
          variant_id: variant.id,
          title: product.title,
          url: `${window.Shopify.routes.root}products/${handle}`,
          image: product.featured_image || (product.images && product.images[0]) || '',
          price: variant.price || null,
          old_price: variant.compare_at_price || null,
          lowest_price: lowest_price,
          is_grouped: child_products && child_products.length > 0 ? true : false,
          out_of_stock: out_of_stock,
          badges: badges || '',
        };

        const labelSet = new Set();

        const isOnSale =
          (p.old_price && p.old_price > p.price) ||
          (p.is_grouped && child_products.some((child) => child.compare_at_price > child.price));

        if (isOnSale) labelSet.add('On Sale');

        if (p.badges) {
          p.badges
            .split(',')
            .map((label) => label.trim())
            .filter((label) => label.length)
            .forEach((label) => labelSet.add(label));
        }

        const badge = Array.from(labelSet)
          .map((label) => {
            const scheme = label.toLowerCase().replace(/\s+/g, '-');
            return `<span class="c-badge c-badge--${scheme}">${label}</span>`;
          })
          .join('');

        const base = p.image.split('?')[0];
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.innerHTML = `
          <div class="card-wrapper product-card-wrapper underline-links-hover">
            <div class="card card--standard card--media" style="--ratio-percent: 100%;">
              <div class="card__inner color-mackenzie gradient ratio" style="--ratio-percent: 100%;">
                <div class="c-badges">
                  ${badge}
                </div>
                <div class="card__media">
                  <div class="media media--transparent media--hover-effect">
                    <img
                      src="${base}?width=533"
                      sizes="${sizes}"
                      alt="${p.title}"
                      class="motion-reduce"
                      loading="lazy"
                      width="600" height="600"
                    >
                  </div>
                </div>
                <div class="card__content">
                  <div class="card__information">
                    <h3 class="card__heading">
                      <a href="${p.url}" class="full-unstyled-link">
                        ${p.title}
                      </a>
                    </h3>
                  </div>
                </div>
              </div>

              <div class="card__content">
                <div class="card__information">
                  <div class="card__heading h5">
                    <a href="${p.url}" class="full-unstyled-link">
                      ${p.title}
                    </a>
                  </div>
                </div>

                <div class="c-custom-card-product__quick-add">
                  <div class="price">
                    <div class="price__container">
                      ${
                        p.old_price
                          ? `<div class="price__old">
                              <span class="visually-hidden visually-hidden--inline">Old price</span>
                              <span class="price-item price-item--old">
                                ${formatMoney(p.old_price)}
                              </span>
                             </div>`
                          : ''
                      }
                      <div class="price__regular ${p.lowest_price ? 'price__starting-at' : ''}">
                        <span class="visually-hidden visually-hidden--inline">Regular price</span>
                          ${p.lowest_price ? `<span class="price__starting-at-label">Starting at</span>` : ''}
                        <span class="price-item price-item--regular ${p.old_price && p.lowest_price == null ? 'price-item--sale' : ''}">
                          ${p.lowest_price ? formatMoney(p.lowest_price) : formatMoney(p.price)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="quick-add no-js-hidden">
                    <product-form>
                      <form method="post" action="/cart/add" class="form" novalidate>
                        <input type="hidden" name="form_type" value="product">
                        <input type="hidden" name="utf8" value="✓">
                        <input type="hidden" name="id" value="${p.variant_id}" class="product-variant-id">
                        ${
                          out_of_stock
                            ? '<span>Out of stock</span>'
                            : p.is_grouped
                              ? `<a href="${p.url}" class="quick-add__submit button button--primary">
                          <span>Shop now</span>
                          <span class="sold-out-message hidden">Sold out</span>
                          <div class="loading__spinner hidden">
                            <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                              <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
                            </svg>
                          </div>
                        </a>`
                              : `<button type="submit" class="quick-add__submit button button--primary">
                          <span>Add to cart</span>
                          <span class="sold-out-message hidden">Sold out</span>
                          <div class="loading__spinner hidden">
                            <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
                              <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
                            </svg>
                          </div>
                        </button>`
                        }
                        <input type="hidden" name="product-id" value="${product.id}">
                      </form>
                    </product-form>
                  </div>
                </div>
              </div>
            </div>
          </div>`;

        this.wrapper.appendChild(slide);
      } catch (e) {
        console.error(`Failed to load product ${handle}:`, e);
      }
    }
  }

  initSwiper() {
    this.swiper = new Swiper(this.sectionEl, {
      slidesPerView: 2,
      spaceBetween: 16,
      breakpoints: {
        767: { slidesPerView: 3 },
      },
      navigation: {
        nextEl: this.sectionEl.querySelector('.swiper-button-next'),
        prevEl: this.sectionEl.querySelector('.swiper-button-prev'),
      },
      pagination: {
        el: this.sectionEl.querySelector('.swiper-pagination'),
        clickable: true,
      },
    });
  }
}

new RecentlyViewedSwiper();
